import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Cookie, Shield } from "lucide-react";

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

type ConsentState = "pending" | "accepted" | "rejected";

const CONSENT_KEY = "rrl_cookie_consent";

function updateGtagConsent(granted: boolean) {
  window.gtag?.("consent", "update", {
    ad_storage: granted ? "granted" : "denied",
    ad_user_data: granted ? "granted" : "denied",
    ad_personalization: granted ? "granted" : "denied",
    analytics_storage: granted ? "granted" : "denied",
  });
}

const CookieConsent = () => {
  const { t } = useTranslation();
  const [state, setState] = useState<ConsentState>("pending");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored === "accepted") {
      updateGtagConsent(true);
      setState("accepted");
    } else if (stored === "rejected") {
      updateGtagConsent(false);
      setState("rejected");
    } else {
      // small delay so it doesn't flash on load
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, "accepted");
    updateGtagConsent(true);
    setState("accepted");
    setVisible(false);
  };

  const handleReject = () => {
    localStorage.setItem(CONSENT_KEY, "rejected");
    updateGtagConsent(false);
    setState("rejected");
    setVisible(false);
  };

  if (state !== "pending" || !visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6 animate-in slide-in-from-bottom-4 duration-500">
      <div className="mx-auto max-w-2xl rounded-xl border border-border/60 bg-card/95 backdrop-blur-xl p-5 sm:p-6 shadow-2xl shadow-black/40">
        <div className="flex items-start gap-4">
          <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <Cookie className="h-5 w-5 text-primary" />
          </div>

          <div className="flex-1 space-y-3">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary sm:hidden" />
              <h3 className="font-mono text-sm font-medium text-foreground tracking-tight">
                {t("cookieConsent.title")}
              </h3>
            </div>

            <p className="text-sm leading-relaxed text-muted-foreground">
              {t("cookieConsent.body")}{" "}
              <a
                href="/privacy"
                className="text-primary hover:text-primary/80 underline underline-offset-2 transition-colors"
              >
                {t("cookieConsent.privacy")}
              </a>
            </p>

            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <button
                onClick={handleAccept}
                className="inline-flex items-center justify-center rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-all hover:bg-primary/90 active:scale-[0.98] font-mono"
              >
                {t("cookieConsent.accept")}
              </button>
              <button
                onClick={handleReject}
                className="inline-flex items-center justify-center rounded-lg border border-border bg-secondary px-5 py-2.5 text-sm font-medium text-secondary-foreground transition-all hover:bg-secondary/80 active:scale-[0.98] font-mono"
              >
                {t("cookieConsent.reject")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
